import React from 'react';
import {
  Page, Section, SectionTitleUnderlined, SectionTitleBare, SkillPills,
  ExperienceList, LineList, prettyLink
} from './templates/_shared';

/**
 * "Creative" template (key: creative-two-column).
 *
 * Modern sidebar layout. Left rail (32% width) holds contact, skills, and
 * meta sections in muted sand on white. Right column (68%) holds the heavy
 * narrative content (summary + experience + education). Best for design,
 * marketing, brand, and product roles where some visual personality helps
 * but ATS-readability still matters. The DOM order is meta→content so ATS
 * scanners get name/contact first.
 */

const ACCENT = '#9a3412';      // burnt orange — warm but professional
const SIDEBAR_BG = '#fef3e8';  // warm sand
const PILL_BG = '#fde6cc';

const CreativeTwoColumn = ({ data }) => {
  const fontFamily = "'Inter', 'Helvetica Neue', Arial, sans-serif";
  return (
    <Page
      accent={ACCENT}
      fontFamily={fontFamily}
      padding="0"
      style={{ lineHeight: 1.45 }}
    >
      <div style={{
        display: 'grid',
        gridTemplateColumns: '32% 68%',
        minHeight: '11in'
      }}>
        {/* === Left rail (sidebar) === */}
        <aside style={{
          background: SIDEBAR_BG,
          padding: '0.75in 0.4in',
          borderRight: `2px solid ${ACCENT}`
        }}>
          <h1 style={{
            fontSize: '20pt',
            fontWeight: 700,
            margin: 0,
            color: ACCENT,
            lineHeight: 1.1
          }}>
            {data.fullName}
          </h1>
          {data.jobTitle && (
            <div style={{
              fontSize: '10pt',
              marginTop: '4px',
              color: '#7c2d12',
              fontWeight: 500
            }}>
              {data.jobTitle}
            </div>
          )}

          {(data.email || data.phone || data.linkedin || data.portfolio) && (
            <Section style={{ marginTop: '1rem' }}>
              <SectionTitleBare accent={ACCENT}>Contact</SectionTitleBare>
              <div style={{ fontSize: '9.5pt', wordBreak: 'break-word' }}>
                {data.email && <div style={{ marginBottom: '2px' }}>{data.email}</div>}
                {data.phone && <div style={{ marginBottom: '2px' }}>{data.phone}</div>}
                {data.linkedin && <div style={{ marginBottom: '2px' }}>{prettyLink(data.linkedin)}</div>}
                {data.portfolio && <div>{prettyLink(data.portfolio)}</div>}
              </div>
            </Section>
          )}

          {data.skills && (
            <Section style={{ marginTop: '0.85rem' }}>
              <SectionTitleBare accent={ACCENT}>Skills</SectionTitleBare>
              <SkillPills skills={data.skills} accent={ACCENT} background={PILL_BG} />
            </Section>
          )}

          {data.education && (
            <Section style={{ marginTop: '0.85rem' }}>
              <SectionTitleBare accent={ACCENT}>Education</SectionTitleBare>
              <LineList text={data.education} />
            </Section>
          )}

          {data.certifications && (
            <Section style={{ marginTop: '0.85rem' }}>
              <SectionTitleBare accent={ACCENT}>Certifications</SectionTitleBare>
              <LineList text={data.certifications} />
            </Section>
          )}

          {data.languages && (
            <Section style={{ marginTop: '0.85rem' }}>
              <SectionTitleBare accent={ACCENT}>Languages</SectionTitleBare>
              <LineList text={data.languages} />
            </Section>
          )}
        </aside>

        {/* === Right column (content) === */}
        <main style={{ padding: '0.75in 0.55in' }}>
          {data.summary && (
            <Section>
              <SectionTitleUnderlined accent={ACCENT}>About</SectionTitleUnderlined>
              <p style={{ margin: 0 }}>{data.summary}</p>
            </Section>
          )}

          {data.experience && (
            <Section style={{ marginTop: '0.85rem' }}>
              <SectionTitleUnderlined accent={ACCENT}>Experience</SectionTitleUnderlined>
              <ExperienceList text={data.experience} accent={ACCENT} />
            </Section>
          )}
        </main>
      </div>
    </Page>
  );
};

export default CreativeTwoColumn;
